/**
 * Simplified ↔ Traditional Chinese, character by character.
 *
 * Data: the character tables of OpenCC (Open Chinese Convert, https://github.com/BYVoid/OpenCC,
 * Apache License 2.0, copyright the OpenCC authors), copied unchanged from the project's
 * `data/dictionary` folder and served from `public/data`:
 *
 * - `opencc-STCharacters.txt` (Simplified → Traditional), last changed upstream on
 *   2026-07-17 (commit 178f05d) — 4,029 lines
 * - `opencc-TSCharacters.txt` (Traditional → Simplified), last changed upstream on
 *   2026-09-16 (commit 5c764a5) — 5,062 lines
 * - `opencc-TWVariants.txt` and `opencc-HKVariants.txt`, the small character tables OpenCC applies
 *   after STCharacters for Taiwan standard and Hong Kong forms (爲 → 為, 裏 → 裡, 着 → 著); optional
 * - `opencc-LICENSE.txt`, the licence text. Each table keeps its own header, which names the
 *   licence and the source.
 *
 * Each line is a character, a tab, and one or more candidates separated by spaces; the first
 * candidate is OpenCC's default. A character with more than one candidate is ambiguous:
 * 发 is 發 (to emit, to develop) or 髮 (hair), 后 is 後 (after) or 后 (empress), 干 is 幹, 乾 or 干.
 * The tool uses the default and lists every ambiguous character so the reader can choose.
 *
 * What a program that works character by character cannot do: choose between the candidates
 * (that needs the word, which OpenCC handles with separate phrase dictionaries that this tool
 * does not use), convert regional vocabulary (软件 / 軟體 / 軟件), or tell which of two valid
 * forms a Hong Kong, Taiwan or mainland text wants.
 */

export type ChineseDirection = "s2t" | "t2s";
export type ChineseRegion = "standard" | "tw" | "hk";

export const CHINESE_DATA_URLS: Record<ChineseDirection, string> = {
  s2t: "/data/opencc-STCharacters.txt",
  t2s: "/data/opencc-TSCharacters.txt",
};

/** The regional variant tables; "standard" has none. */
export const CHINESE_VARIANT_URLS: Record<
  Exclude<ChineseRegion, "standard">,
  string
> = {
  tw: "/data/opencc-TWVariants.txt",
  hk: "/data/opencc-HKVariants.txt",
};

/** Character → candidates, default first. */
export type CharacterTable = Map<string, string[]>;

const LINE_BREAK = /\r?\n/;
const SPACES = / +/;

/** Parses an OpenCC character table (`key<TAB>candidate candidate …`; `#` starts a comment). */
export function parseCharacterTable(text: string): CharacterTable {
  const table: CharacterTable = new Map();
  for (const line of text.split(LINE_BREAK)) {
    if (line === "" || line.startsWith("#")) {
      continue;
    }
    const [key, values] = line.split("\t");
    if (!(key && values)) {
      continue;
    }
    const candidates = values.split(SPACES).filter(Boolean);
    if (candidates.length > 0) {
      table.set(key, candidates);
    }
  }
  return table;
}

export interface AmbiguousCharacter {
  /** The character in the input. */
  char: string;
  /** How many times it occurs. */
  count: number;
  /** The candidates, OpenCC's default first. */
  candidates: string[];
  /** True when the input character itself is one of the candidates (it is valid as it stands). */
  validAsIs: boolean;
}

export interface ChineseResult {
  text: string;
  /** Characters that were replaced. */
  changed: number;
  /** Characters with more than one candidate. */
  ambiguous: AmbiguousCharacter[];
  /** Chinese characters that have no entry (already in the target form, or outside the table). */
  unchanged: number;
}

const HAN = /\p{Script=Han}/u;

function pickCandidate(
  candidates: readonly string[],
  wanted: string | undefined
): string {
  return wanted !== undefined && candidates.includes(wanted)
    ? wanted
    : (candidates[0] as string);
}

function noteAmbiguous(
  found: Map<string, AmbiguousCharacter>,
  char: string,
  candidates: string[]
): void {
  const seen = found.get(char);
  if (seen) {
    seen.count += 1;
  } else {
    found.set(char, {
      char,
      count: 1,
      candidates,
      validAsIs: candidates.includes(char),
    });
  }
}

/**
 * Converts text one character at a time. `choices` maps an ambiguous character to the
 * candidate to use for every occurrence of it; any other ambiguous character gets the default.
 * `variants` is an optional regional table (Taiwan or Hong Kong forms) applied afterwards.
 */
export function convertCharacters(
  input: string,
  table: CharacterTable,
  choices: Readonly<Record<string, string>> = {},
  variants?: CharacterTable
): ChineseResult {
  const found = new Map<string, AmbiguousCharacter>();
  let out = "";
  let changed = 0;
  let unchanged = 0;
  for (const char of input) {
    const candidates = table.get(char);
    if (candidates && candidates.length > 1) {
      noteAmbiguous(found, char, candidates);
    }
    const base = candidates ? pickCandidate(candidates, choices[char]) : char;
    const final = variants?.get(base)?.[0] ?? base;
    out += final;
    if (final !== char) {
      changed += 1;
    } else if (candidates || HAN.test(char)) {
      unchanged += 1;
    }
  }
  return { text: out, changed, ambiguous: [...found.values()], unchanged };
}

/** The same text with every ambiguous character shown as 【default|other|…】. */
export function markAmbiguous(
  input: string,
  table: CharacterTable,
  choices: Readonly<Record<string, string>> = {},
  variants?: CharacterTable
): string {
  const regional = (char: string) => variants?.get(char)?.[0] ?? char;
  let out = "";
  for (const char of input) {
    const candidates = table.get(char);
    if (candidates && candidates.length > 1) {
      const chosen = pickCandidate(candidates, choices[char]);
      const rest = candidates.filter((candidate) => candidate !== chosen);
      out += `【${[chosen, ...rest].map(regional).join("|")}】`;
    } else {
      out += regional(candidates ? (candidates[0] as string) : char);
    }
  }
  return out;
}

let cache = new Map<string, Promise<CharacterTable>>();

/** Fetches and parses a table once per page; a failed fetch can be retried. */
export function loadTable(url: string): Promise<CharacterTable> {
  const existing = cache.get(url);
  if (existing) {
    return existing;
  }
  const request = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`OpenCC table returned ${response.status}`);
      }
      return response.text();
    })
    .then(parseCharacterTable)
    .catch((error: unknown) => {
      cache = new Map([...cache].filter(([key]) => key !== url));
      throw error;
    });
  cache.set(url, request);
  return request;
}

/** The character table for a direction. */
export function loadCharacterTable(
  direction: ChineseDirection
): Promise<CharacterTable> {
  return loadTable(CHINESE_DATA_URLS[direction]);
}

/** The regional variant table, or an empty one for the standard forms. */
export async function loadVariantTable(
  region: ChineseRegion
): Promise<CharacterTable | undefined> {
  if (region === "standard") {
    return;
  }
  return await loadTable(CHINESE_VARIANT_URLS[region]);
}
